import React, { useCallback, useEffect, useRef, useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';

// Context
import { useDataSettingsContext } from '../../context';

// Components
import VerificationInput from '../../../../../../components/VerificationInput';
import Button from '../../../../../../components/CustomButton';

// Styles
import viewsStyles from '../views.module.scss';

// Utils
import { emailVerificationVerifyUrl } from '../../../../../../utils/urls';
import useCsrfToken from '../../../../../../utils/hooks/csfrToken';

interface ViewEmailVerificationProps {
  email: string;
  loading: boolean;
  onResendVerificationCode: () => Promise<void>;
  onSuccess: () => void;
}

const ViewEmailVerification: React.FC<ViewEmailVerificationProps> = ({ email, loading, onResendVerificationCode, onSuccess }) => {
  const { t } = useTranslation();

  // Context
  const { onSetHeaderSubtitle, onUpdateUser } = useDataSettingsContext();

  // Utils
  const csrfToken = useCsrfToken();

  // States
  const [verifying, setVerifying] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const [verificationCodeError, setVerificationCodeError] = useState<string | null>(null);
  const [verificationCodeTimer, setVerificationCodeTimer] = useState<number>(60);

  // Ref
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Effects
  useEffect(() => {
    onSetHeaderSubtitle(t('profile.settings.data.change_email.verify_email_description', { email }));

    if (verificationCodeTimer > 0) {
      timerRef.current = setTimeout(() => {
        setVerificationCodeTimer(prev => prev - 1);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [verificationCodeTimer, onSetHeaderSubtitle]);

  // Handlers
  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setVerifying(true);
    if (!verificationCode) {
      setVerifying(false);
      throw new Error('Verification code is required');
    }

    try {
      const formData = new FormData();
      formData.append('verification_code', verificationCode);

      const response = await fetch(emailVerificationVerifyUrl(), {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });

      if (response.ok) {
        if (onUpdateUser) {
          onUpdateUser({ email });
        }
        onSuccess();
      } else {
        const data = await response.json().catch(() => ({}));
        const errorMsg = data?.error || 'Invalid verification code';
        setVerificationCodeError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error(error);
      setVerificationCodeError('Invalid verification code');
    } finally {
      setVerifying(false);
    }
  }, [verificationCode, csrfToken, onSuccess]);

  const handleChangeVerificationCode = useCallback((value: string) => {
    setVerificationCode(value);
    setVerificationCodeError(null);
  }, []);

  const handleResendVerificationCode = useCallback(async () => {
    await onResendVerificationCode();

    // Reset timer
    setVerificationCodeTimer(60);
  }, []);

  return (
    <div className={viewsStyles.container}>
      <form onSubmit={handleSubmit} className={viewsStyles.form} id='change-email-verification-code-form'>
        <VerificationInput onlyNumbers error={verificationCodeError} length={4} onChange={handleChangeVerificationCode} />

        <div className={`${viewsStyles.actions} ${viewsStyles.actions_column}`}>
          <Button
            variant='primary'
            type='submit'
            classes={{
              root: viewsStyles.actions_button,
            }}
            disabled={loading || !verificationCode || verifying}
          >
            {t('profile.settings.data.change_email.verify_email')}
          </Button>

          <Button
            variant='secondary'
            classes={{
              root: viewsStyles.actions_button,
            }}
            disabled={loading || verificationCodeTimer > 0 || verifying}
            onClick={handleResendVerificationCode}
          >
            {verificationCodeTimer > 0 ?
              `${t('profile.settings.data.change_email.resend_verification_email')} (${verificationCodeTimer})` :
              t('profile.settings.data.change_email.resend_verification_email')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ViewEmailVerification;
